import React from "react";
import { useNavigate } from "react-router-dom";

const Ecosystem = () => {
  const navigate = useNavigate();

  const ecosystemFeatures = [
    {
      id: "green-staking",
      icon: "🌿",
      title: "Green Staking",
      description: "Earn rewards while supporting sustainable blockchain infrastructure with our eco-friendly proof-of-authority consensus.",
      features: ["Low energy consumption", "Instant finality", "Carbon neutral"]
    },
    {
      id: "nft-marketplace",
      icon: "💎",
      title: "NFT Marketplace",
      description: "Trade digital assets on our energy-efficient marketplace with minimal environmental impact.",
      features: ["Gas-efficient minting", "Royalty protection", "Cross-chain compatibility"]
    },
    {
      id: "defi-protocols",
      icon: "🔄",
      title: "DeFi Protocols",
      description: "Access decentralized finance with lightning-fast transactions and minimal carbon footprint.",
      features: ["Near-zero fees", "High throughput", "Sustainable yield farming"]
    },
    {
      id: "cross-chain-bridge",
      icon: "🌐",
      title: "Cross-Chain Bridge",
      description: "Seamlessly transfer assets between networks with our eco-conscious bridging solution.",
      features: ["Multi-chain support", "Secure transfers", "Energy optimized"]
    },
    {
      id: "governance",
      icon: "🏛️",
      title: "Governance",
      description: "Participate in network decisions with our democratic governance system.",
      features: ["Token voting", "Proposal system", "Transparent process"]
    },
    {
      id: "developer-tools",
      icon: "🔧",
      title: "Developer Tools",
      description: "Build next-gen dApps with our comprehensive developer toolkit and documentation.",
      features: ["SDK & APIs", "Testing environment", "Deployment tools"]
    }
  ];

  const partners = [
    { name: "Green Crypto Fund", logo: "🌱", description: "Sustainable Investment" },
    { name: "Eco Web3 Labs", logo: "🔬", description: "Research & Development" },
    { name: "Clean Chain Alliance", logo: "🤝", description: "Industry Partnership" },
    { name: "Renewable Tech", logo: "⚡", description: "Energy Solutions" }
  ];

  const handleExploreClick = (featureId) => {
    console.log(`Navigating to: /ecosystem/${featureId}`);
    navigate(`/ecosystem/${featureId}`);
  };

  const handleStartBuilding = () => {
    console.log("Navigating to developers page");
    navigate("/developers");
  };

  const handleViewDocs = () => {
    console.log("Navigating to documentation");
    navigate("/documentation");
  };

  return (
    <section id="ecosystem" className="py-20 bg-gradient-to-br from-eco-black to-eco-dark relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-eco-green to-eco-blue"></div>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0, 255, 136, 0.3) 1px, transparent 0)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black gradient-text mb-4">
            X1 Ecosystem
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Discover the comprehensive suite of sustainable Web3 solutions built on X EcoChain. 
            From DeFi to NFTs, our ecosystem prioritizes environmental responsibility without compromising performance.
          </p>
        </div>

        {/* Ecosystem Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {ecosystemFeatures.map((feature, index) => (
            <div
              key={feature.id}
              className="glass-card p-6 rounded-3xl border border-eco-green border-opacity-20 hover:border-eco-green hover:border-opacity-40 transition-all duration-500 group hover:transform hover:scale-105 cursor-pointer"
              onClick={() => handleExploreClick(feature.id)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleExploreClick(feature.id);
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={`Explore ${feature.title}`}
            >
              {/* Icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-eco-green to-eco-blue rounded-2xl flex items-center justify-center text-eco-black text-2xl mb-4 group-hover:scale-110 transition-transform duration-300">
                {feature.icon}
              </div>

              {/* Content */}
              <h3 className="text-xl font-black text-white mb-3 group-hover:text-eco-green transition-colors">
                {feature.title}
              </h3>
              <p className="text-gray-400 mb-4 leading-relaxed">
                {feature.description}
              </p>

              {/* Features List */}
              <ul className="space-y-2">
                {feature.features.map((item, idx) => (
                  <li key={idx} className="flex items-center text-sm text-eco-blue">
                    <span className="w-2 h-2 bg-eco-blue rounded-full mr-3 flex-shrink-0"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button 
                className="w-full mt-6 py-3 bg-eco-dark border border-eco-green border-opacity-30 rounded-xl text-eco-green font-semibold hover:bg-eco-green hover:bg-opacity-10 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-eco-green focus:ring-opacity-50"
                onClick={(e) => {
                  e.stopPropagation();
                  handleExploreClick(feature.id);
                }}
              >
                Explore {feature.title}
              </button>
            </div>
          ))}
        </div>

        {/* Ecosystem Stats */}
        <div className="glass-card rounded-3xl p-6 md:p-8 mb-16 border border-eco-green border-opacity-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-center">
            <div className="p-4">
              <div className="text-2xl md:text-3xl font-black gradient-text">50+</div>
              <div className="text-gray-400 text-sm md:text-base">dApps Live</div>
            </div>
            <div className="p-4">
              <div className="text-2xl md:text-3xl font-black text-eco-green">99.5%</div>
              <div className="text-gray-400 text-sm md:text-base">Uptime</div>
            </div>
            <div className="p-4">
              <div className="text-2xl md:text-3xl font-black text-eco-blue">2.1M</div>
              <div className="text-gray-400 text-sm md:text-base">Daily Transactions</div>
            </div>
            <div className="p-4">
              <div className="text-2xl md:text-3xl font-black text-eco-purple">85%</div>
              <div className="text-gray-400 text-sm md:text-base">Energy Reduction</div>
            </div>
          </div>
        </div>

        {/* Partners Section */}
        <div className="text-center mb-16">
          <h3 className="text-2xl font-black text-white mb-8">Trusted by Industry Leaders</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {partners.map((partner, index) => (
              <div
                key={index}
                className="glass-card p-4 md:p-6 rounded-2xl border border-gray-700 hover:border-eco-green hover:border-opacity-40 transition-all duration-300 group"
              >
                <div className="text-2xl md:text-3xl mb-3 group-hover:scale-110 transition-transform duration-300">
                  {partner.logo}
                </div>
                <h4 className="font-bold text-white mb-1 text-sm md:text-base">{partner.name}</h4>
                <p className="text-xs md:text-sm text-gray-400">{partner.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="glass-card p-6 md:p-8 rounded-3xl border border-eco-green border-opacity-30 max-w-2xl mx-auto">
            <h3 className="text-2xl font-black gradient-text mb-4">
              Ready to Build on X EcoChain?
            </h3>
            <p className="text-gray-300 mb-6 text-base md:text-lg">
              Join thousands of developers creating the future of sustainable Web3 applications.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                className="px-6 md:px-8 py-3 bg-eco-green text-eco-black font-bold rounded-xl transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-eco-green focus:ring-opacity-50"
                onClick={handleStartBuilding}
              >
                Start Building
              </button>
              <button 
                className="px-6 md:px-8 py-3 bg-eco-blue text-eco-black font-bold rounded-xl transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-eco-blue focus:ring-opacity-50"
                onClick={handleViewDocs}
              >
                View Documentation
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Ecosystem;